/**
 * Connectors page component.
 * Displays user's connections and allows adding new connectors.
 * Only visible in preview mode.
 *
 * @module pages/ConnectorsPage
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plug,
  Plus,
  Clock,
  Trash2,
  AlertCircle,
  Check,
  Loader2,
  X,
  ExternalLink,
  RefreshCw,
  ArrowLeft,
  Search,
  Key,
  Shield,
  Wrench,
  Box,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { LoadingPage, ConfirmModal } from '@/components/ui';
import {
  createUserScopedAdapter,
  type ConnectionServiceAdapter,
} from '@/services/connectionServiceAdapter';
import { useAuth } from '@/contexts/AuthContext';
import type {
  ConnectionResponse,
  ConnectionTypeResponse,
  PolicyRule,
  PolicyHookDefinition,
  ToolGroupDefinition,
} from '@/types';

/** Auto-refresh interval in milliseconds (1 minute) */
const AUTO_REFRESH_INTERVAL = 60_000;

/** Get display name for a connection type, falling back to name if displayName is empty */
const getDisplayName = (type: ConnectionTypeResponse): string =>
  type.displayName?.trim() || type.name;

/** Connector type card used in the add connector grid */
function ConnectorTypeCard({
  type,
  onClick,
  disabled,
}: {
  type: ConnectionTypeResponse;
  onClick: (type: ConnectionTypeResponse) => void;
  disabled: boolean;
}) {
  return (
    <button
      onClick={() => onClick(type)}
      disabled={disabled}
      className="flex items-center gap-3 p-4 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] hover:border-gray-300 dark:hover:border-[#3a3a3a] hover:bg-gray-50 dark:hover:bg-[#161616] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
    >
      <div className="flex-shrink-0 h-10 w-10 bg-gray-50 dark:bg-[#161616] rounded-lg flex items-center justify-center overflow-hidden">
        {type.iconUrl ? (
          <img
            src={type.iconUrl}
            alt={getDisplayName(type)}
            className="h-6 w-6 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <Plug className={`h-5 w-5 text-gray-400 ${type.iconUrl ? 'hidden' : ''}`} />
      </div>
      <span className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-200 truncate">
        {getDisplayName(type)}
      </span>
    </button>
  );
}

interface ConnectorsPageProps {
  /** Adapter for connection service calls — user-scoped or group-scoped */
  serviceAdapter?: ConnectionServiceAdapter;
  /** When true, hides the page header (for use as an embedded tab) */
  embedded?: boolean;
}

/**
 * Connectors page - shows user's connections and allows adding new ones.
 * Can be used standalone at /connectors or embedded inside another page (e.g. SandboxGroupDetailPage).
 */
export function ConnectorsPage({ serviceAdapter, embedded }: ConnectorsPageProps = {}) {
  const adapter = useMemo(() => serviceAdapter ?? createUserScopedAdapter(), [serviceAdapter]);
  const { user } = useAuth();
  const [connections, setConnections] = useState<ConnectionResponse[]>([]);
  const [connectionTypes, setConnectionTypes] = useState<ConnectionTypeResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddMode, setIsAddMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<ConnectionTypeResponse | null>(null);
  const [connectionName, setConnectionName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectionToDelete, setConnectionToDelete] = useState<ConnectionResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // State for parameter-based authorization dialog
  const [authorizingConnection, setAuthorizingConnection] = useState<ConnectionResponse | null>(
    null
  );
  const [parameterValues, setParameterValues] = useState<Record<string, string>>({});

  // State for parameter value set selection (for connectors with multiple auth types)
  const [selectedParameterValueSet, setSelectedParameterValueSet] = useState<string | null>(null);
  // State for parameter values within the selected parameter value set
  const [parameterValueSetValues, setParameterValueSetValues] = useState<Record<string, string>>(
    {}
  );

  // State for policy rules and enabled tool groups during connection creation
  const [createPolicyRules, setCreatePolicyRules] = useState<PolicyRule[]>([]);
  const [createEnabledGroups, setCreateEnabledGroups] = useState<string[] | null>(null);

  // State for policy rules editing dialog on existing connections
  const [editingPoliciesConnection, setEditingPoliciesConnection] =
    useState<ConnectionResponse | null>(null);
  const [editPolicyRules, setEditPolicyRules] = useState<PolicyRule[]>([]);
  const [editEnabledGroups, setEditEnabledGroups] = useState<string[] | null>(null);
  const [isSavingPolicies, setIsSavingPolicies] = useState(false);

  // Refresh all connections
  const refreshAllConnections = useCallback(async () => {
    if (connections.length === 0) return;

    console.log('[ConnectorsPage] Auto-refreshing all connections...');
    try {
      // Refresh each connection in parallel (skip virtual connectors that aren't in CosmosDB)
      const refreshedConnections = await Promise.all(
        connections.map(async (conn) => {
          if (conn.deletable === false) return conn; // Virtual connectors are always Ready
          try {
            return await adapter.refresh(conn.id);
          } catch {
            // If refresh fails for one, keep the existing connection
            return conn;
          }
        })
      );
      setConnections(refreshedConnections);
    } catch (err) {
      console.error('Failed to refresh connections:', err);
    }
  }, [connections, adapter]);

  // Fetch connections and connection types, then refresh all connection states
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [connectionsData, typesData] = await Promise.all([
          adapter.list(),
          adapter.listTypes(),
        ]);
        setConnectionTypes(typesData);

        // Refresh each connection to get latest state, but preserve usedBySandboxIds from list
        if (connectionsData.length > 0) {
          console.log('[ConnectorsPage] Refreshing all connection states...');
          const refreshedConnections = await Promise.all(
            connectionsData.map(async (conn) => {
              if (conn.deletable === false) return conn; // Virtual connectors are always Ready
              try {
                return await adapter.refresh(conn.id);
              } catch {
                return conn; // Keep original if refresh fails
              }
            })
          );
          setConnections(refreshedConnections);
        } else {
          setConnections(connectionsData);
        }
      } catch (err) {
        console.error('Failed to load connectors data:', err);
        setError('Failed to load connectors');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [adapter]);

  // Auto-refresh connections every minute
  useEffect(() => {
    if (isLoading || connections.length === 0) return;

    const intervalId = setInterval(() => {
      refreshAllConnections();
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isLoading, connections.length, refreshAllConnections]);

  const handleSelectType = async (type: ConnectionTypeResponse) => {
    // For OAuth connectors, do everything in one click: create + open consent link
    // For parameter-based or parameter value set connectors, show the dialog
    // If the connector has policy hooks, always show the dialog so user can configure them
    const hasParameterValueSets = type.parameterValueSets && type.parameterValueSets.length > 0;
    const isParameterBased =
      type.authType === 'parameters' && type.parameters && type.parameters.length > 0;
    const hasPolicyHooks = type.policyHooks && type.policyHooks.length > 0;
    const hasToolGroups = type.toolGroups && type.toolGroups.length > 0;

    if (hasParameterValueSets || isParameterBased || hasPolicyHooks || hasToolGroups) {
      // Show dialog for complex auth flows or policy configuration
      setSelectedType(type);
      setConnectionName(getDisplayName(type));
      setCreatePolicyRules([]);
      setCreateEnabledGroups(null);
      if (hasParameterValueSets) {
        setSelectedParameterValueSet(type.parameterValueSets![0].name);
        const firstSet = type.parameterValueSets![0];
        if (firstSet.parameters && firstSet.parameters.length > 0) {
          const initialValues: Record<string, string> = {};
          for (const param of firstSet.parameters) {
            initialValues[param.name] = '';
          }
          setParameterValueSetValues(initialValues);
        } else {
          setParameterValueSetValues({});
        }
      } else {
        setSelectedParameterValueSet(null);
        setParameterValueSetValues({});
      }
      return;
    }

    // OAuth flow: create connection and authorize if needed
    setIsCreating(true);
    try {
      const newConnection = await adapter.create({
        type: type.name,
        name: getDisplayName(type),
      });
      setConnections((prev) => [...prev, newConnection]);

      // If connection is already Ready (e.g., first-party auth succeeded), no OAuth needed
      if (newConnection.state === 'Ready') {
        setIsCreating(false);
        return;
      }

      // Generate consent link and redirect
      const redirectUrl = embedded ? window.location.href : `${window.location.origin}/connectors`;
      const consentLink = await adapter.generateConsentLink(newConnection.id, redirectUrl);
      window.location.href = consentLink;
    } catch (err) {
      console.error('Failed to create and authorize connection:', err);
      setError('Failed to create connection');
      setIsCreating(false);
    }
  };

  const handleParameterValueSetChange = (setName: string) => {
    setSelectedParameterValueSet(setName);
    // Initialize parameter values for the new set
    if (selectedType?.parameterValueSets) {
      const selectedSet = selectedType.parameterValueSets.find((s) => s.name === setName);
      if (selectedSet?.parameters && selectedSet.parameters.length > 0) {
        const initialValues: Record<string, string> = {};
        for (const param of selectedSet.parameters) {
          initialValues[param.name] = '';
        }
        setParameterValueSetValues(initialValues);
      } else {
        setParameterValueSetValues({});
      }
    }
  };

  const handleCreateConnection = async () => {
    if (!selectedType || !connectionName.trim()) return;

    setIsCreating(true);
    try {
      const newConnection = await adapter.create({
        type: selectedType.name,
        name: connectionName.trim(),
        parameterValueSetName: selectedParameterValueSet ?? undefined,
        parameterValueSetValues:
          Object.keys(parameterValueSetValues).length > 0 ? parameterValueSetValues : undefined,
        policyRules: createPolicyRules.length > 0 ? createPolicyRules : undefined,
        enabledToolGroups: createEnabledGroups ?? undefined,
      });

      setConnections((prev) => [...prev, newConnection]);

      // If connection needs authorization (not Ready)
      if (newConnection.state !== 'Ready') {
        const typeInfo = connectionTypes.find((t) => t.name === selectedType.name);

        // If this is NOT a parameter-based connector, redirect to OAuth
        if (typeInfo?.authType !== 'parameters') {
          const redirectUrl = embedded
            ? window.location.href
            : `${window.location.origin}/connectors`;
          const consentLink = await adapter.generateConsentLink(newConnection.id, redirectUrl);
          window.location.href = consentLink;
          return;
        }
      }

      // Clear form state
      setSelectedType(null);
      setConnectionName('');
      setSelectedParameterValueSet(null);
      setParameterValueSetValues({});
      setCreatePolicyRules([]);
      setCreateEnabledGroups(null);
      setIsAddMode(false);
      setSearchQuery('');
    } catch (err) {
      console.error('Failed to create connection:', err);
      setError('Failed to create connection');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setSelectedType(null);
    setConnectionName('');
    setSelectedParameterValueSet(null);
    setParameterValueSetValues({});
    setCreatePolicyRules([]);
    setCreateEnabledGroups(null);
  };

  const handleDeleteConnection = (connection: ConnectionResponse) => {
    // If sandboxes reference this connection, show a warning before proceeding
    if (connection.usedBySandboxIds && connection.usedBySandboxIds.length > 0) {
      setConnectionToDelete(connection);
    } else {
      void doDeleteConnection(connection.id, false);
    }
  };

  const doDeleteConnection = async (id: string, force: boolean) => {
    setIsDeleting(true);
    try {
      await adapter.delete(id, force);
      setConnections((prev) => prev.filter((c) => c.id !== id));
      setConnectionToDelete(null);
    } catch (err: unknown) {
      // If the API returns 409 Conflict, the connection is in use by sandboxes.
      // Show the confirmation dialog so the user can force-delete.
      const axiosErr = err as { response?: { status?: number; data?: { sandboxIds?: string[] } } };
      if (axiosErr.response?.status === 409) {
        const sandboxIds = axiosErr.response.data?.sandboxIds ?? [];
        const conn = connections.find((c) => c.id === id);
        if (conn) {
          setConnectionToDelete({ ...conn, usedBySandboxIds: sandboxIds });
        }
      } else {
        console.error('Failed to delete connection:', err);
        setError('Failed to delete connection');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAuthorize = async (connection: ConnectionResponse) => {
    // If connection is already Ready, no need to authorize
    if (connection.state === 'Ready') {
      return;
    }

    const typeInfo = getTypeInfo(connection.type);

    // For parameter-based connectors, show the parameter input dialog
    if (
      typeInfo?.authType === 'parameters' &&
      typeInfo.parameters &&
      typeInfo.parameters.length > 0
    ) {
      setAuthorizingConnection(connection);
      // Initialize parameter values
      const initialValues: Record<string, string> = {};
      for (const param of typeInfo.parameters) {
        initialValues[param.name] = '';
      }
      setParameterValues(initialValues);
      return;
    }

    // For OAuth connectors, use the consent link flow
    setIsAuthorizing(connection.id);
    try {
      // Redirect back to the connectors page after consent
      const redirectUrl = embedded ? window.location.href : `${window.location.origin}/connectors`;
      const consentLink = await adapter.generateConsentLink(connection.id, redirectUrl);
      // Redirect to the consent link
      window.location.href = consentLink;
    } catch (err) {
      console.error('Failed to generate consent link:', err);
      setError('Failed to generate authorization link');
      setIsAuthorizing(null);
    }
  };

  const handleParameterAuthorize = async () => {
    if (!authorizingConnection) return;

    setIsAuthorizing(authorizingConnection.id);
    try {
      const updatedConnection = await adapter.authorize(authorizingConnection.id, {
        parameterValues,
      });
      setConnections((prev) =>
        prev.map((c) => (c.id === authorizingConnection.id ? updatedConnection : c))
      );
      setAuthorizingConnection(null);
      setParameterValues({});
    } catch (err) {
      console.error('Failed to authorize connection:', err);
      setError('Failed to authorize connection');
    } finally {
      setIsAuthorizing(null);
    }
  };

  const handleCancelParameterAuth = () => {
    setAuthorizingConnection(null);
    setParameterValues({});
  };

  const handleEditPolicies = (connection: ConnectionResponse) => {
    setEditingPoliciesConnection(connection);
    setEditPolicyRules(connection.policyRules ? [...connection.policyRules] : []);
    setEditEnabledGroups(connection.enabledToolGroups ? [...connection.enabledToolGroups] : null);
  };

  const handleSavePolicies = async () => {
    if (!editingPoliciesConnection) return;

    setIsSavingPolicies(true);
    try {
      const updatedConnection = await adapter.updatePolicyRules(
        editingPoliciesConnection.id,
        editPolicyRules,
        editEnabledGroups ?? undefined
      );
      setConnections((prev) =>
        prev.map((c) => (c.id === editingPoliciesConnection.id ? updatedConnection : c))
      );
      setEditingPoliciesConnection(null);
      setEditPolicyRules([]);
      setEditEnabledGroups(null);
    } catch (err) {
      console.error('Failed to update policy rules:', err);
      setError('Failed to update policy rules');
    } finally {
      setIsSavingPolicies(false);
    }
  };

  const handleCancelEditPolicies = () => {
    setEditingPoliciesConnection(null);
    setEditPolicyRules([]);
    setEditEnabledGroups(null);
  };

  const handleRefreshConnection = async (id: string) => {
    setIsRefreshing(id);
    try {
      const refreshedConnection = await adapter.refresh(id);
      setConnections((prev) => prev.map((c) => (c.id === id ? refreshedConnection : c)));
    } catch (err) {
      console.error('Failed to refresh connection:', err);
      setError('Failed to refresh connection');
    } finally {
      setIsRefreshing(null);
    }
  };

  // Create a lookup map for connection types by name
  const typeInfoMap = useMemo(() => {
    const map = new Map<string, ConnectionTypeResponse>();
    for (const type of connectionTypes) {
      map.set(type.name.toLowerCase(), type);
    }
    return map;
  }, [connectionTypes]);

  // Get the connection type info
  const getTypeInfo = useCallback(
    (typeName: string): ConnectionTypeResponse | undefined => {
      return typeInfoMap.get(typeName.toLowerCase());
    },
    [typeInfoMap]
  );

  // Get the icon URL for a connection type
  const getIconUrl = useCallback(
    (typeName: string): string | undefined => {
      return getTypeInfo(typeName)?.iconUrl;
    },
    [getTypeInfo]
  );

  // Filter connection types based on search query (searches both name and displayName)
  // Only show stable connectors that haven't been added yet
  const filteredConnectionTypes = useMemo(() => {
    // Get types that don't already have a connection
    const availableTypes = connectionTypes.filter((t) => {
      if (t.stability !== 'stable') return false;
      // Check if there's already a connection of this type
      const hasExisting = connections.some((c) => c.type.toLowerCase() === t.name.toLowerCase());
      return !hasExisting;
    });
    if (!searchQuery.trim()) {
      return availableTypes;
    }
    const query = searchQuery.toLowerCase();
    return availableTypes.filter(
      (type) =>
        type.name.toLowerCase().includes(query) ||
        getDisplayName(type).toLowerCase().includes(query)
    );
  }, [connectionTypes, connections, searchQuery]);

  // Split filtered types into personal and service connectors
  const personalTypes = useMemo(
    () => filteredConnectionTypes.filter((t) => t.isPersonal),
    [filteredConnectionTypes]
  );
  const serviceTypes = useMemo(
    () => filteredConnectionTypes.filter((t) => !t.isPersonal),
    [filteredConnectionTypes]
  );

  // Split existing connections into personal and service for the list view
  const personalConnectorTypeNames = useMemo(() => {
    const names = new Set<string>();
    for (const t of connectionTypes) {
      if (t.isPersonal) names.add(t.name.toLowerCase());
    }
    return names;
  }, [connectionTypes]);

  const personalConnections = useMemo(
    () => connections.filter((c) => personalConnectorTypeNames.has(c.type.toLowerCase())),
    [connections, personalConnectorTypeNames]
  );
  const serviceConnections = useMemo(
    () => connections.filter((c) => !personalConnectorTypeNames.has(c.type.toLowerCase())),
    [connections, personalConnectorTypeNames]
  );

  const userEmail = user?.email ?? 'your account';

  /** Renders a single connection row in the list table */
  const ConnectionRow = useCallback(
    ({ connection }: { connection: ConnectionResponse }) => (
      <tr key={connection.id} className="hover:bg-gray-50 dark:hover:bg-[#161616]">
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 h-8 w-8 bg-gray-100 dark:bg-[#2a2a2a] rounded-lg flex items-center justify-center overflow-hidden">
              {getIconUrl(connection.type) ? (
                <img
                  src={getIconUrl(connection.type)}
                  alt={connection.type}
                  className="h-6 w-6 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <Plug
                className={`h-4 w-4 text-gray-500 ${getIconUrl(connection.type) ? 'hidden' : ''}`}
              />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                {connection.name || connection.type}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{connection.type}</div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center gap-2">
            {getStatusIcon(connection.state)}
            <span className="text-sm text-gray-600 dark:text-gray-400">{connection.state}</span>
            {(connection.state === 'Creating' || connection.state === 'Error') && (
              <button
                onClick={() => handleAuthorize(connection)}
                disabled={isAuthorizing === connection.id}
                className="ml-2 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAuthorizing === connection.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : getTypeInfo(connection.type)?.authType === 'parameters' ? (
                  <Key className="h-3 w-3" />
                ) : (
                  <ExternalLink className="h-3 w-3" />
                )}
                Authorize
              </button>
            )}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          {connection.usedBySandboxIds && connection.usedBySandboxIds.length > 0 ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-full">
              <Box className="h-3 w-3" />
              {connection.usedBySandboxIds.length} sandbox
              {connection.usedBySandboxIds.length !== 1 ? 'es' : ''}
            </span>
          ) : (
            <span className="text-sm text-gray-400 dark:text-gray-500">None</span>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <Clock className="h-3.5 w-3.5" />
            {connection.createdAt
              ? formatDistanceToNow(new Date(connection.createdAt), { addSuffix: true })
              : '-'}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right">
          <div className="flex items-center justify-end gap-1">
            {connection.deletable !== false && (
              <button
                onClick={() => handleRefreshConnection(connection.id)}
                disabled={isRefreshing === connection.id}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
                title="Refresh connection state"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isRefreshing === connection.id ? 'animate-spin' : ''}`}
                />
              </button>
            )}
            {(getTypeInfo(connection.type)?.policyHooks?.length ?? 0) > 0 ||
            (getTypeInfo(connection.type)?.toolGroups?.length ?? 0) > 0 ? (
              <button
                onClick={() => handleEditPolicies(connection)}
                className="p-2 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                title="Edit policies & tools"
              >
                <Shield className="h-4 w-4" />
              </button>
            ) : null}
            {connection.deletable !== false && (
              <button
                onClick={() => handleDeleteConnection(connection)}
                className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                title="Delete connection"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthorizing, isRefreshing, getIconUrl, getTypeInfo]
  );

  /** Renders the policy rules editor for a given set of hooks */
  const renderPolicyRulesEditor = (
    hooks: PolicyHookDefinition[],
    rules: PolicyRule[],
    setRules: (rules: PolicyRule[]) => void
  ) => {
    const addRule = (hookId: string) => {
      setRules([...rules, { hookId, patterns: [''] }]);
    };

    const removeRule = (index: number) => {
      setRules(rules.filter((_, i) => i !== index));
    };

    const updatePattern = (ruleIndex: number, patternIndex: number, value: string) => {
      const updated = [...rules];
      const patterns = [...updated[ruleIndex].patterns];
      patterns[patternIndex] = value;
      updated[ruleIndex] = { ...updated[ruleIndex], patterns };
      setRules(updated);
    };

    const addPattern = (ruleIndex: number) => {
      const updated = [...rules];
      updated[ruleIndex] = {
        ...updated[ruleIndex],
        patterns: [...updated[ruleIndex].patterns, ''],
      };
      setRules(updated);
    };

    const removePattern = (ruleIndex: number, patternIndex: number) => {
      const updated = [...rules];
      const patterns = updated[ruleIndex].patterns.filter((_, i) => i !== patternIndex);
      updated[ruleIndex] = { ...updated[ruleIndex], patterns };
      if (patterns.length === 0) {
        // Remove the whole rule if no patterns left
        setRules(rules.filter((_, i) => i !== ruleIndex));
      } else {
        setRules(updated);
      }
    };

    return (
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Policy Rules
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Configure deny/allow patterns for this connector&apos;s tools.
        </p>

        {/* Existing rules */}
        {rules.map((rule, ruleIndex) => {
          const hook = hooks.find((h) => h.id === rule.hookId);
          return (
            <div
              key={ruleIndex}
              className="border border-gray-200 dark:border-[#2a2a2a] rounded-lg p-3 space-y-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                    {hook?.description || rule.hookId}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      hook?.hookType === 'FieldValueDenyList'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    }`}
                  >
                    {hook?.hookType === 'FieldValueDenyList' ? 'Deny' : 'Allow'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeRule(ruleIndex)}
                  className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                  aria-label="Remove rule"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {rule.patterns.map((pattern, patternIndex) => (
                <div key={patternIndex} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={pattern}
                    onChange={(e) => updatePattern(ruleIndex, patternIndex, e.target.value)}
                    placeholder="e.g., *@example.com"
                    className="flex-1 px-3 py-1.5 text-sm bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-md text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => removePattern(ruleIndex, patternIndex)}
                    className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    aria-label="Remove pattern"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addPattern(ruleIndex)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                + Add pattern
              </button>
            </div>
          );
        })}

        {/* Add rule buttons for hooks that don't have a rule yet — deny hooks first, then allow hooks */}
        {hooks
          .filter((hook) => !rules.some((r) => r.hookId === hook.id))
          .sort((a, b) => {
            // Deny hooks first, then allow hooks
            if (a.hookType === b.hookType) return 0;
            return a.hookType === 'FieldValueDenyList' ? -1 : 1;
          })
          .map((hook) => (
            <button
              key={hook.id}
              type="button"
              onClick={() => addRule(hook.id)}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border border-dashed border-gray-300 dark:border-[#3a3a3a] rounded-lg hover:border-gray-400 dark:hover:border-[#4a4a4a] hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              {hook.description}
              <span
                className={`ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  hook.hookType === 'FieldValueDenyList'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                }`}
              >
                {hook.hookType === 'FieldValueDenyList' ? 'Deny' : 'Allow'}
              </span>
            </button>
          ))}
      </div>
    );
  };

  /** Renders a tool group checklist for enabling/disabling capability groups */
  const renderToolGroupChecklist = (
    groups: ToolGroupDefinition[],
    enabledGroups: string[] | null,
    setEnabledGroups: (groups: string[] | null) => void
  ) => {
    // null means all enabled (default)
    const allEnabled = enabledGroups === null;
    const enabledSet = allEnabled ? new Set(groups.map((g) => g.key)) : new Set(enabledGroups);

    const toggleGroup = (key: string) => {
      if (allEnabled) {
        // Switching from "all enabled" to explicit list with this one removed
        setEnabledGroups(groups.map((g) => g.key).filter((k) => k !== key));
      } else if (enabledSet.has(key)) {
        setEnabledGroups(enabledGroups!.filter((k) => k !== key));
      } else {
        const next = [...(enabledGroups ?? []), key];
        // If everything is now enabled, go back to null (all enabled)
        if (next.length === groups.length) {
          setEnabledGroups(null);
        } else {
          setEnabledGroups(next);
        }
      }
    };

    const enabledCount = enabledSet.size;

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Enabled Capabilities
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {enabledCount} of {groups.length} capabilities enabled
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEnabledGroups(null)}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              Enable all
            </button>
            <span className="text-xs text-gray-300 dark:text-gray-600">|</span>
            <button
              type="button"
              onClick={() => setEnabledGroups([])}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              Disable all
            </button>
          </div>
        </div>

        <div className="border border-gray-200 dark:border-[#2a2a2a] rounded-lg divide-y divide-gray-200 dark:divide-[#2a2a2a]">
          {groups.map((group) => {
            const isEnabled = enabledSet.has(group.key);
            return (
              <label
                key={group.key}
                className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#161616] transition-colors"
              >
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={() => toggleGroup(group.key)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-[#3a3a3a] text-gray-900 dark:text-white focus:ring-gray-900 dark:focus:ring-white bg-white dark:bg-[#0a0a0a]"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                    {group.displayName}
                  </div>
                  {group.description && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {group.description}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const getStatusIcon = (state: string) => {
    switch (state) {
      case 'Ready':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'Error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'Creating':
        return <Loader2 className="h-4 w-4 text-yellow-500 animate-spin" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  // Full-page Add Connector view
  if (isAddMode) {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => {
              setIsAddMode(false);
              setSearchQuery('');
            }}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Add Connector</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select a connector to add to your account
            </p>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search connectors..."
            className="w-full pl-10 pr-4 py-3 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent"
            autoFocus
            aria-label="Search connectors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Results count */}
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {filteredConnectionTypes.length} connector
          {filteredConnectionTypes.length !== 1 ? 's' : ''} available
          {searchQuery && ` matching "${searchQuery}"`}
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="h-4 w-4" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto" aria-label="Dismiss error">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Connector grid */}
        <div className="flex-1 overflow-y-auto">
          {filteredConnectionTypes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Search className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                No connectors found
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Try a different search term
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Personal Connectors */}
              {personalTypes.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    Personal Connectors
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    These connectors act on behalf of <strong>{userEmail}</strong> — they access
                    your personal data like mail, calendar, and files.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {personalTypes.map((type) => (
                      <ConnectorTypeCard
                        key={type.name}
                        type={type}
                        onClick={handleSelectType}
                        disabled={isCreating}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Service Connectors */}
              {serviceTypes.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    Service Connectors
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    These connectors provide AI coding tools and service integrations.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {serviceTypes.map((type) => (
                      <ConnectorTypeCard
                        key={type.name}
                        type={type}
                        onClick={handleSelectType}
                        disabled={isCreating}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Name Input Dialog */}
        {selectedType && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
            onMouseDown={handleCancelCreate}
          >
            <div
              className="bg-white dark:bg-[#161616] rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col"
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-connector-dialog-title"
            >
              {/* Dialog Header */}
              <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-[#2a2a2a]">
                <div className="flex-shrink-0 h-10 w-10 bg-gray-50 dark:bg-[#0a0a0a] rounded-lg flex items-center justify-center overflow-hidden">
                  {selectedType.iconUrl ? (
                    <img
                      src={selectedType.iconUrl}
                      alt={getDisplayName(selectedType)}
                      className="h-6 w-6 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <Plug
                    className={`h-5 w-5 text-gray-400 ${selectedType.iconUrl ? 'hidden' : ''}`}
                  />
                </div>
                <div className="flex-1">
                  <h2
                    id="add-connector-dialog-title"
                    className="text-lg font-semibold text-gray-900 dark:text-white"
                  >
                    Add {getDisplayName(selectedType)}
                  </h2>
                </div>
                <button
                  onClick={handleCancelCreate}
                  className="p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  aria-label="Close dialog"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Dialog Content */}
              <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Connection Name
                  </label>
                  <input
                    type="text"
                    value={connectionName}
                    onChange={(e) => setConnectionName(e.target.value)}
                    placeholder="Enter a name for this connection"
                    className="w-full px-4 py-3 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && connectionName.trim()) {
                        handleCreateConnection();
                      } else if (e.key === 'Escape') {
                        handleCancelCreate();
                      }
                    }}
                  />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    This name will help you identify the connection later.
                  </p>
                </div>

                {/* Parameter Value Set Selection (for connectors with multiple auth types) */}
                {selectedType.parameterValueSets && selectedType.parameterValueSets.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Authentication Method
                    </label>
                    <select
                      value={selectedParameterValueSet ?? ''}
                      onChange={(e) => handleParameterValueSetChange(e.target.value)}
                      className="w-full px-4 py-3 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent"
                    >
                      {selectedType.parameterValueSets.map((pvs) => (
                        <option key={pvs.name} value={pvs.name}>
                          {pvs.displayName || pvs.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Choose how you want to authenticate with this connector.
                    </p>
                  </div>
                )}

                {/* Parameter inputs for the selected parameter value set */}
                {selectedType.parameterValueSets &&
                  selectedParameterValueSet &&
                  (() => {
                    const selectedSet = selectedType.parameterValueSets.find(
                      (s) => s.name === selectedParameterValueSet
                    );
                    if (!selectedSet?.parameters || selectedSet.parameters.length === 0) {
                      return null;
                    }
                    return selectedSet.parameters.map((param) => (
                      <div key={param.name}>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {param.displayName || param.name}
                          {param.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                          type={param.type === 'securestring' ? 'password' : 'text'}
                          value={parameterValueSetValues[param.name] || ''}
                          onChange={(e) =>
                            setParameterValueSetValues((prev) => ({
                              ...prev,
                              [param.name]: e.target.value,
                            }))
                          }
                          placeholder={
                            param.description || `Enter ${param.displayName || param.name}`
                          }
                          className="w-full px-4 py-3 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent"
                        />
                        {param.description && (
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {param.description}
                          </p>
                        )}
                      </div>
                    ));
                  })()}

                {/* Policy Rules (for connectors with policy hooks) */}
                {selectedType.policyHooks && selectedType.policyHooks.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-[#2a2a2a] pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="h-4 w-4 text-blue-500" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Policy Rules
                      </h3>
                      <span className="text-xs text-gray-400">(optional)</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      Restrict which values this connector's tools can use. You can also configure
                      these later.
                    </p>
                    {renderPolicyRulesEditor(
                      selectedType.policyHooks,
                      createPolicyRules,
                      setCreatePolicyRules
                    )}
                  </div>
                )}

                {/* Tool Checklist (for connectors with tools) */}
                {selectedType.toolGroups && selectedType.toolGroups.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-[#2a2a2a] pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Wrench className="h-4 w-4 text-blue-500" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Capabilities
                      </h3>
                      <span className="text-xs text-gray-400">(optional)</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      Choose which capabilities this connector exposes. Disabled capabilities will
                      not be available in sandboxes.
                    </p>
                    {renderToolGroupChecklist(
                      selectedType.toolGroups,
                      createEnabledGroups,
                      setCreateEnabledGroups
                    )}
                  </div>
                )}
              </div>

              {/* Dialog Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-[#2a2a2a]">
                <button
                  onClick={handleCancelCreate}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateConnection}
                  disabled={!connectionName.trim() || isCreating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Create Connection
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main connectors list view
  return (
    <div className="h-full">
      {/* Header — hidden when embedded inside another page */}
      <div
        className={`flex items-center ${embedded ? 'justify-end mb-4' : 'justify-between mb-6'}`}
      >
        {!embedded && (
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Connectors</h1>
        )}
        <button
          onClick={() => setIsAddMode(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Connector
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto" aria-label="Dismiss error">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Connections Table */}
      <div className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" aria-label="Connectors">
            <thead className="bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-[#2a2a2a]">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Connector
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Sandboxes
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Created
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#0a0a0a] divide-y divide-gray-200 dark:divide-[#2a2a2a]">
              {connections.length === 0 ? (
                <tr className="bg-white dark:bg-[#0a0a0a]">
                  <td colSpan={5} className="px-6 py-32">
                    <div className="flex flex-col items-center justify-center text-center">
                      <Plug className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                        No Connectors yet
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Connect to external services to use them in your sandboxes.
                      </p>
                      <button
                        onClick={() => setIsAddMode(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        Add Connector
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {/* Personal Connectors section */}
                  {personalConnections.length > 0 && (
                    <>
                      <tr className="bg-gray-50/50 dark:bg-[#111]">
                        <td colSpan={5} className="px-6 py-2">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Personal Connectors
                          </span>
                          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 normal-case tracking-normal">
                            Acts as {userEmail}
                          </span>
                        </td>
                      </tr>
                      {personalConnections.map((connection) => (
                        <ConnectionRow key={connection.id} connection={connection} />
                      ))}
                    </>
                  )}
                  {/* Service Connectors section */}
                  {serviceConnections.length > 0 && (
                    <>
                      <tr className="bg-gray-50/50 dark:bg-[#111]">
                        <td colSpan={5} className="px-6 py-2">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Service Connectors
                          </span>
                        </td>
                      </tr>
                      {serviceConnections.map((connection) => (
                        <ConnectionRow key={connection.id} connection={connection} />
                      ))}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation modal — shown when the connection is still referenced by sandboxes */}
      <ConfirmModal
        isOpen={connectionToDelete !== null}
        onClose={() => setConnectionToDelete(null)}
        onConfirm={() => connectionToDelete && void doDeleteConnection(connectionToDelete.id, true)}
        title="Delete connection"
        message={
          connectionToDelete?.usedBySandboxIds?.length
            ? `This connection is used by ${connectionToDelete.usedBySandboxIds.length} sandbox(es). Deleting it will remove the reference from those sandboxes and they will lose access to it. Are you sure?`
            : 'Are you sure you want to delete this connection?'
        }
        confirmText="Delete anyway"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Parameter Authorization Dialog */}
      {authorizingConnection && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
          onMouseDown={handleCancelParameterAuth}
        >
          <div
            className="bg-white dark:bg-[#161616] rounded-xl shadow-xl w-full max-w-md mx-4"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="authorize-dialog-title"
          >
            {/* Dialog Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-[#2a2a2a]">
              <div className="flex-shrink-0 h-10 w-10 bg-gray-50 dark:bg-[#0a0a0a] rounded-lg flex items-center justify-center overflow-hidden">
                {getIconUrl(authorizingConnection.type) ? (
                  <img
                    src={getIconUrl(authorizingConnection.type)}
                    alt={authorizingConnection.type}
                    className="h-6 w-6 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <Key
                  className={`h-5 w-5 text-gray-400 ${getIconUrl(authorizingConnection.type) ? 'hidden' : ''}`}
                />
              </div>
              <div className="flex-1">
                <h2
                  id="authorize-dialog-title"
                  className="text-lg font-semibold text-gray-900 dark:text-white"
                >
                  Authorize {authorizingConnection.name}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Enter the required credentials
                </p>
              </div>
              <button
                onClick={handleCancelParameterAuth}
                className="p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                aria-label="Close dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Dialog Content - Parameter Fields */}
            <div className="px-6 py-4 space-y-4">
              {getTypeInfo(authorizingConnection.type)?.parameters?.map((param) => (
                <div key={param.name}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {param.displayName || param.name}
                    {param.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type={param.type === 'securestring' ? 'password' : 'text'}
                    value={parameterValues[param.name] || ''}
                    onChange={(e) =>
                      setParameterValues((prev) => ({
                        ...prev,
                        [param.name]: e.target.value,
                      }))
                    }
                    placeholder={param.description || `Enter ${param.displayName || param.name}`}
                    className="w-full px-4 py-2 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent"
                  />
                  {param.description && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {param.description}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Dialog Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-[#2a2a2a]">
              <button
                onClick={handleCancelParameterAuth}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleParameterAuthorize}
                disabled={isAuthorizing === authorizingConnection.id}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAuthorizing === authorizingConnection.id ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Authorizing...
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4" />
                    Authorize
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Policy Rules Dialog */}
      {editingPoliciesConnection && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
          onMouseDown={handleCancelEditPolicies}
        >
          <div
            className="bg-white dark:bg-[#161616] rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-policies-dialog-title"
          >
            {/* Dialog Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-[#2a2a2a]">
              <div className="flex-shrink-0 h-10 w-10 bg-gray-50 dark:bg-[#0a0a0a] rounded-lg flex items-center justify-center overflow-hidden">
                {getIconUrl(editingPoliciesConnection.type) ? (
                  <img
                    src={getIconUrl(editingPoliciesConnection.type)}
                    alt={editingPoliciesConnection.type}
                    className="h-6 w-6 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <Shield
                  className={`h-5 w-5 text-gray-400 ${getIconUrl(editingPoliciesConnection.type) ? 'hidden' : ''}`}
                />
              </div>
              <div className="flex-1">
                <h2
                  id="edit-policies-dialog-title"
                  className="text-lg font-semibold text-gray-900 dark:text-white"
                >
                  Policies &amp; Tools
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {editingPoliciesConnection.name}
                </p>
              </div>
              <button
                onClick={handleCancelEditPolicies}
                className="p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                aria-label="Close dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Dialog Content */}
            <div className="px-6 py-4 overflow-y-auto flex-1 space-y-6">
              {/* Policy Rules Section */}
              {(getTypeInfo(editingPoliciesConnection.type)?.policyHooks ?? []).length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="h-4 w-4 text-blue-500" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Policy Rules
                    </h3>
                  </div>
                  {renderPolicyRulesEditor(
                    getTypeInfo(editingPoliciesConnection.type)?.policyHooks ?? [],
                    editPolicyRules,
                    setEditPolicyRules
                  )}
                </div>
              )}

              {/* Tool Group Checklist Section */}
              {(getTypeInfo(editingPoliciesConnection.type)?.toolGroups ?? []).length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Wrench className="h-4 w-4 text-blue-500" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Capabilities
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Choose which capabilities this connector exposes. Disabled capabilities will not
                    be available in sandboxes.
                  </p>
                  {renderToolGroupChecklist(
                    getTypeInfo(editingPoliciesConnection.type)!.toolGroups!,
                    editEnabledGroups,
                    setEditEnabledGroups
                  )}
                </div>
              )}
            </div>

            {/* Dialog Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-[#2a2a2a]">
              <button
                onClick={handleCancelEditPolicies}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePolicies}
                disabled={isSavingPolicies}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingPolicies ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
